import { View } from 'react-native'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  HelperText,
  IconButton,
  Text,
  TextInput,
} from 'react-native-paper'
import * as Sentry from '@sentry/react-native'
import Column from '../../../@ui/containers/grid/column'
import { createStyle } from '../../../contexts/global-styles.provider'
import {
  useApplyCouponToBillingMutation,
  useProcessCouponBillingMutation,
  useRemoveCouponFromBillingMutation,
} from '../../../generated/graphql'
import { getFirstGraphQlErrorMessage } from '../../../utils/error-utils'

export type CouponPreview = {
  billingId: string
  amount: number
  basePrice?: number | null
  couponCode?: string | null
  discountPercentage?: number | null
  appliedMonths?: number | null
  isForever: boolean
  isFree: boolean
}

type PaymentCouponSectionProps = {
  billingId: string
  prefilledCoupon: string
  onCouponChange: (coupon: CouponPreview | null) => void
  onFreeCouponProcessed: () => void
}

export const PaymentCouponSection = ({
  billingId,
  prefilledCoupon,
  onCouponChange,
  onFreeCouponProcessed,
}: PaymentCouponSectionProps) => {
  const { t } = useTranslation('node-tier')
  const styles = useStyles()
  // States
  const [couponCode, setCouponCode] = useState(prefilledCoupon)
  const [couponError, setCouponError] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<CouponPreview | null>(null)
  // Mutations
  const [applyCouponToBilling, { loading: applyingCoupon }] =
    useApplyCouponToBillingMutation()
  const [removeCouponFromBilling, { loading: removingCoupon }] =
    useRemoveCouponFromBillingMutation()
  const [processCouponBilling, { loading: processingFreeCoupon }] =
    useProcessCouponBillingMutation()
  const isCouponBusy = applyingCoupon || removingCoupon || processingFreeCoupon

  // On Init
  useEffect(() => {
    setCouponCode(prefilledCoupon)
    setCouponError('')
    setAppliedCoupon(null)
  }, [billingId, prefilledCoupon])

  // Use Effects
  const getCouponErrorMessage = (error: unknown) => {
    const gqlError = getFirstGraphQlErrorMessage(error)
    if (gqlError) return gqlError
    if (error instanceof Error && error.message) return error.message
    return t('coupon-apply-failed')
  }

  const handleApplyCoupon = async () => {
    setCouponError('')
    const trimmed = couponCode.trim()
    if (!trimmed) {
      setCouponError(t('coupon-code-required'))
      return
    }
    try {
      const { data } = await applyCouponToBilling({
        variables: { billingId, couponCode: trimmed },
      })
      const preview = data?.applyCouponToBilling
      if (!preview) {
        setCouponError(t('coupon-apply-failed'))
        return
      }
      const nextCoupon: CouponPreview = {
        billingId: preview.billingId,
        amount: preview.amount,
        basePrice: preview.basePrice,
        couponCode: preview.couponCode,
        discountPercentage: preview.discountPercentage,
        appliedMonths: preview.appliedMonths,
        isForever: preview.isForever,
        isFree: preview.isFree,
      }
      setAppliedCoupon(nextCoupon)
      setCouponCode(nextCoupon.couponCode ?? trimmed)
      onCouponChange(nextCoupon)
    } catch (error) {
      console.error('applyCouponToBilling failed', error)
      Sentry.captureException(error)
      setCouponError(getCouponErrorMessage(error))
    }
  }

  const handleRemoveCoupon = async () => {
    setCouponError('')
    try {
      const { data } = await removeCouponFromBilling({
        variables: { billingId },
      })
      const preview = data?.removeCouponFromBilling
      if (!preview) {
        return
      }
      setAppliedCoupon(null)
      setCouponCode('')
      onCouponChange(null)
    } catch (error) {
      console.error('removeCouponFromBilling failed', error)
      Sentry.captureException(error)
      setCouponError(getCouponErrorMessage(error))
    }
  }

  const handlePurchaseWithFreeCoupon = async () => {
    if (!appliedCoupon?.couponCode) {
      return
    }
    setCouponError('')
    try {
      const result = await processCouponBilling({
        variables: {
          billingId,
          couponCode: appliedCoupon.couponCode,
        },
      })
      if (!result.data?.processCouponBilling) {
        setCouponError(t('coupon-apply-failed'))
        return
      }
      onFreeCouponProcessed()
    } catch (error) {
      console.error('processCouponBilling failed', error)
      Sentry.captureException(error)
      setCouponError(getCouponErrorMessage(error))
    }
  }

  return (
    <Column gap={2} style={styles.couponSection}>
      {!appliedCoupon ? (
        <View style={styles.couponApplyRow}>
          <View style={styles.couponInputWrap}>
            <TextInput
              mode="outlined"
              dense
              label={t('coupon-code-placeholder')}
              value={couponCode}
              onChangeText={(value) => {
                setCouponCode(value)
                if (!couponError) {
                  return
                }
                setCouponError('')
              }}
              autoCapitalize="characters"
              autoCorrect={false}
              editable={!isCouponBusy}
              error={!!couponError}
              style={styles.couponInput}
            />
          </View>
          <IconButton
            icon={applyingCoupon ? 'loading' : 'check'}
            mode="contained-tonal"
            size={20}
            style={styles.couponApplyIcon}
            onPress={handleApplyCoupon}
            disabled={isCouponBusy}
            accessibilityLabel={t('apply-coupon')}
          />
        </View>
      ) : (
        <View style={styles.appliedCouponBox}>
          <View style={styles.appliedCouponHeader}>
            <Text variant="titleSmall">
              {t('coupon-applied-title', {
                code: appliedCoupon.couponCode ?? couponCode,
              })}
            </Text>
            <IconButton
              icon="close"
              size={18}
              onPress={handleRemoveCoupon}
              disabled={isCouponBusy}
              accessibilityLabel={t('coupon-remove')}
            />
          </View>
          {appliedCoupon.isFree ? (
            <Button
              mode="contained"
              onPress={handlePurchaseWithFreeCoupon}
              loading={processingFreeCoupon}
              disabled={isCouponBusy}
              style={{ marginTop: 8 }}
            >
              {t('coupon-complete-purchase')}
            </Button>
          ) : null}
        </View>
      )}
      <HelperText type="error" visible={!!couponError}>
        {couponError}
      </HelperText>
    </Column>
  )
}

const useStyles = createStyle(({ theme }) => ({
  couponSection: {
    width: '100%',
    marginTop: 2,
  },
  couponApplyRow: {
    width: '100%',
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  /** minWidth 0 lets flex shrink so the row does not overflow the modal horizontally */
  couponInputWrap: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  couponInput: {
    width: '100%',
  },
  couponApplyIcon: {
    margin: 0,
  },
  appliedCouponBox: {
    width: '100%',
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    borderColor: theme.colors.outlineVariant,
    backgroundColor: theme.colors.surfaceVariant,
    gap: 4,
  },
  appliedCouponHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))
